<template>
  <div class="faq-view">
    <div dir="auto" class="faq-content px-4 px-md-6 px-lg-6 py-3">
      <h2 class="mt-12 mb-6" v-html="$t('FAQ.title')" />
      <div class="pic-block">
        <v-img :lazy-src="faqPic" :src="faqPic" class="faq-pic" alt="faq-pic" />
      </div>
      <v-tabs
        v-model="activeTab"
        class="activity-report-type-toggle my-4"
        height="42"
        hide-slider
        :show-arrows="isNarrow"
      >
        <v-tab
          v-for="(section, index) in faqSections"
          :key="index"
          class="text-none"
        >
          {{ section.category }}
        </v-tab>
      </v-tabs>

      <v-tabs-items v-model="activeTab" class="flex-grow-1">
        <v-tab-item
          v-for="(section, index) in faqSections"
          :key="`${section}-${index}`"
        >
          <v-expansion-panels flat tile>
            <v-expansion-panel
              v-for="(qa, qaIndex) in section.questions"
              :key="`qa-${qaIndex}`"
              class="faq-item-block"
            >
              <v-expansion-panel-header>
                {{ qa.question }}
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                {{ qa.answer }}
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-tab-item>
      </v-tabs-items>

      <div class="additional-block my-8" v-html="additionalText" />
    </div>
    <AppFooter />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

import AppFooter from '@/components/views/AppFooter/AppFooter.vue';
import images from '@/assets/images.js';
import BrandsEnum from '@shared/enums/BrandsEnum.mjs';

export default {
  name: 'FAQView',
  components: {
    AppFooter
  },
  data() {
    return {
      activeTab: null
    };
  },
  jsonld() {
    const faqPage = {
      '@context': this.clientReaderUrl,
      '@type': 'FAQPage',
      mainEntity: []
    };
    this.faqSections.forEach(section => {
      section.questions.slice(0, 4).forEach(q => {
        faqPage.mainEntity.push({
          '@type': 'Question',
          name: q.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: q.answer
          }
        });
      });
    });
    return faqPage;
  },
  computed: {
    ...mapGetters('MediaDetectorStore', ['mediaSize', 'additionalMediaSize']),
    isOcean() {
      const brand = this.$store.getters['ContextStore/brand'];
      return brand === BrandsEnum.OCEAN;
    },
    isNarrow() {
      const { narrow } = this.mediaSize;
      return narrow;
    },
    clientReaderUrl() {
      return this.$store.getters['ContextStore/getClientReaderUrl'];
    },
    clientReaderHost() {
      return this.$store.getters['ContextStore/getClientReaderHost'];
    },
    getSupportEmail() {
      return this.$store.getters['ContextStore/getSupportEmail'];
    },
    additionalText() {
      const baseText = this.$t('FAQ.additional.text');
      const emailLink = `<a href="mailto:${this.getSupportEmail}">${this.getSupportEmail}</a>`;
      return baseText.replace('<supportEmail>', emailLink);
    },
    faqPic() {
      return this.isOcean ? images.faqPicOcean : images.faqPicFfa;
    },
    faqData() {
      return this.isOcean
        ? this.$t('FAQ.ocean', { returnObjects: true })
        : this.$t('FAQ.ffa', { returnObjects: true });
    },
    faqSections() {
      const sections = [];
      const faqData = this.faqData;

      Object.keys(faqData).forEach(category => {
        const questions = faqData[category];
        const section = {
          category,
          questions: questions.map(q => ({
            question: q.question,
            answer: q.answer
          }))
        };

        sections.push(section);
      });

      return sections;
    }
  }
};
</script>

<style lang="less">
@import '~@/assets/styles/base.less';

.faq-content {
  .v-tab {
    font-size: 18px;

    &,
    &:before {
      border-radius: 24px;
    }
  }
  .theme--light.v-tabs > .v-tabs-bar .v-tab:not(.v-tab--active) {
    color: #475569;
  }

  .v-tab.v-tab--active {
    background: var(--primary-color);
    color: var(--white-color);
  }

  .v-tabs-bar__content {
    gap: 4px;
  }

  .v-slide-group__next,
  .v-slide-group__prev {
    flex-basis: 42px;
    min-width: 42px;
  }

  .v-slide-group__prev {
    justify-content: flex-start;
  }

  .v-slide-group__next {
    justify-content: flex-end;
  }

  .v-slide-group__prev--disabled,
  .v-slide-group__next--disabled {
    opacity: 0.5;
  }

  .v-expansion-panel--active {
    &,
    &:not(:first-child) {
      &,
      + .v-expansion-panel {
        margin: 0;
      }
    }
  }

  .v-expansion-panels {
    max-width: 792px;
    border-top: 1px solid #e2e8f0;

    .v-expansion-panel {
      border-bottom: 1px solid #e2e8f0;
    }

    .v-expansion-panel-header,
    .v-expansion-panel-content__wrap {
      padding-left: 0;
      padding-right: 0;
    }

    .v-expansion-panel-header {
      font-size: 18px;
      line-height: 26px;
      font-weight: 700;
      min-height: 64px;
      letter-spacing: -0.02em;

      svg {
        fill: #0f172a;
      }
    }

    .v-expansion-panel-content {
      font-size: 16px;
      line-height: 24px;

      @media @small {
        font-size: 16px;
        line-height: 24px;
      }
    }
  }

  .additional-block {
    a {
      color: var(--secondary-accent-color);
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }
  }
}
</style>

<style scoped lang="less" src="./FAQView.less"></style>
